<div class="row">
  <mat-card>
    <mat-card-content>
      <p>
        SituView allows builders to display all of their homes as panoramas including every home, facade, floorplan
        option and theme combination. The SituView interface allows a web developer to make simple API requests and have
        the panorama, floorplan, navigation and product highlights automatically shown to the user across any platform
        and
        device. The instructions below give you a step by step guide for implementing this functionality. If you have
        any
        problems please reach out to <a href="mailto:web@situsystems.com">web@situsystems.com</a> with questions,
        feedback
        or suggestions.
      </p>

      <h3>Follow step by step or skip ahead.</h3>

      <mat-vertical-stepper #stepper>
        <mat-step label="Installation">
          <h2>JavaScript</h2>
          <p>Include our library on your page.</p>
          <pre><code class="language-markup">&lt;script src="https://view.situsystems.com/v2/situsystems-situview.js">&#60;/script></code></pre>
          <div>
            <button mat-flat-button matStepperNext color="accent">Next</button>
          </div>
        </mat-step>
        <mat-step label="Configuration">
          <p>Bare bones configuration for getting SituView up and running.</p>
          <h2>Markup</h2>
          <p>Configure your target elements on the page.</p>
          <pre>
        <code class="language-markup">
          &lt;div id="SituPanoramaViewer">&#60;/div>
          &lt;div id="SituInteractiveGroundFloorplan">&#60;/div>
        </code>
      </pre>


          <h2>JavaScript</h2>
          <p>Configure SituView settings</p>
          <pre>
        <code class="language-js">
          //create an instance of SituView
          var situView = SituSystems.SituView;

          //create default settings
          var defaultPanoramaSettings = new SituSystems.PanoramaSettings();
          var defaultFloorplanSettings = new SituSystems.FloorplanSettings()

          //add a panorama viewer plugin
          situView.addPlugin(
            new SituSystems.PanoramaViewer(
              "SituPanoramaViewer", //id of our panorama div configured earlier
              defaultPanoramaSettings
            )
          );

          //add an interactive floor plan plugin
          situView.addPlugin(
            new SituSystems.SVGFloorPlan(
              "SituInteractiveGroundFloorplan", //id of our interactive floor plan div configured earlier
              SituSystems.Floors.GROUND,
              true, //true = interactive
              defaultFloorplanSettings
            )
          );

          //specify a home to load on initialisation
          situView.onInit = function () &#123;
            situView.load(&#123;
              homeConfig: &#123;
                builder: "Builder",
                home: "Home Name",
                masterFile: "Master File",
                themes: ["Theme"],
                options: ["Option 1"],
              },
              startingRoom: "kitchen"
            });
          };

          //initialise SituView
          situView.init();
        </code>
      </pre>
          <div>
            <button mat-flat-button matStepperPrevious color="primary">Back</button>
            <button mat-flat-button matStepperNext color="accent">Next</button>
          </div>
        </mat-step>
        <mat-step label="Done">
          <h2>Result</h2>
          <p class="codepen" data-height="650" data-default-tab="js,result" data-slug-hash="ExvePmr"
            data-user="situsystems"
            style="height: 650px; box-sizing: border-box; display: flex; align-items: center; justify-content: center; border: 2px solid; margin: 1em 0; padding: 1em;">
            <span>See the Pen <a href="https://codepen.io/situsystems/pen/ExvePmr">
                SituView v2</a> (<a href="https://codepen.io/situsystems">@situsystems</a>)
              on <a href="https://codepen.io">CodePen</a>.</span>
          </p>

          <br />
          <a class="demo-link" target="_blank" href="https://codepen.io/situsystems/pen/dyVNaKG">
            View Fully Featured Example
          </a>
          <br />
          <br />
          <div>
            <button mat-flat-button matStepperPrevious color="primary">Back</button>
            <button mat-flat-button matStepperNext color="accent">Next</button>
          </div>
        </mat-step>
        <mat-step>
          <ng-template matStepLabel>Explore</ng-template>
          <h2>Reference</h2>
          <h3>SituView</h3>
          <pre>
        <code class="language-js">
          SituView &#123;

            onInit: () => void;

            onError: (error: Error) => void;

            addPlugin(plugin: Plugin);

            addEventListener(type: string, callback: EventListenerOrEventListenerObject | null, options?: AddEventListenerOptions | boolean): void;

            load(&#123;
              homeConfig: &#123;
                  builder: string,
                  home: string,
                  masterFile: string,
                  themes: string[],
                  options: string[],
              },
              startingRoom: string,
              roomSettings: [&#123; name: string, viewingAngle: number }],
              forceRefresh: boolean,
              renderType: string = "BestAvailable", // valid values are Rasterized, BestAvailable
              environment: string = "Production" // valid values are Production, Staging, Fallback, Latest
            });

            isFullScreen();

            openFullScreen();

            closeFullScreen();

            toggleFullScreen();

            isGyroEnabled();

            enableGyro();

            disableGyro();

            toggleGyro();
          }
        </code>
      </pre>

          <h3>Plugins</h3>
          <pre>
        <code class="language-js">
          //create an instance of SituView
          var situView = SituSystems.SituView;

          //PanoramaViewer
          PanoramaViewer(divName: string, settings: PanoramaSettings)
          //example
          situView.addPlugin(new SituSystems.PanoramaViewer("SituPanoramaViewer", panoramaSettings));

          //SVGFloorPlan
          SVGFloorPlan(divName: string, floor: Floors, interactive: boolean, settings: FloorplanSettings)
          //example
          situView.addPlugin(new SituSystems.SVGFloorPlan("SituStaticFirstFloorPlan", SituSystems.Floors.FIRST, false, staticFPSettings));

          //LoadStatus
          LoadStatus(divName: string)
          //example
          situView.addPlugin(new SituSystems.LoadStatus("situLoadStatus"));

          //LoadProgress
          LoadProgress(divName: string)
          //example
          situView.addPlugin(new SituSystems.LoadProgress("situLoadProgress"));

          //RoomList
          RoomList(divName: string)
          //example
          situView.addPlugin(new SituSystems.RoomList("situRooms"));
        </code>
      </pre>

          <h3>Listeners</h3>
          <pre>
        <code class="language-js">
          //create an instance of SituView
          var situView = SituSystems.SituView;

          // attach event listeners
          situView.addEventListener("unhandlederror", function (e) &#123;
            console.log("unhandlederror " + e.message);
          });

          situView.addEventListener("pluginadded", function (e) &#123;
            console.log("pluginadded " + e.detail.plugin.name);
          });

          situView.addEventListener("pluginremoved", function (e) &#123;
            console.log("pluginremoved " + e.detail.plugin.name);
          });

          situView.addEventListener("homeloaderror", function (e) &#123;
            console.log("homeloaderror " + e.message);
          });

          situView.addEventListener("roomselected", function (e) &#123;
            console.log("roomselected " + JSON.stringify(e.detail));
          });

          situView.addEventListener("init", function (e) &#123;
            console.log("init");
          });

          situView.addEventListener("floorplanavailable", function (e) &#123;
            console.log("floorplanavailable " + JSON.stringify(e.detail));
          });

          situView.addEventListener("roompanoramaavailable", function (e) &#123;
            // fires when the first panorama for a room is available
            console.log("roompanoramaavailable " + JSON.stringify(e.detail));
          });

          situView.addEventListener("homeconfigloadprogress", function (e) &#123;
            console.log("homeconfigloadprogress " + JSON.stringify(e.detail));
          });

          situView.addEventListener("homereset", function (e) &#123;
            console.log("homereset");
          });

          situView.addEventListener("viewinganglechange", function (e) &#123;
            console.log("viewinganglechange " + JSON.stringify(e.detail));
          });

          situView.addEventListener("homeDetails", function (e) &#123;
            console.log("homeDetails " + JSON.stringify(e.detail));
            // emits the artifactId which can be used to construct a permalink
            var artifactId = e.detail.artifactId;
            var permalink = "https://view.situlink.com/" + artifactId;
          });
        </code>
      </pre>

          <h3>Settings</h3>
          <h4>Panorama Settings</h4>
          <pre>
        <code class="language-js">
          PanoramaSettings &#123;
            overlayElementId?: string = null;

            imageSize: number = 4096; // Typically 1024, 2048, 4096

            fieldOfView: number = 85;
            fieldOfViewType: FieldOfViewType | undefined = undefined;
            minFieldOfView: number = 40;
            maxFieldOfView: number = 110;
            adjustableFieldOfView: boolean = false;

            calloutHotspotVisibility: HotspotVisibility = HotspotVisibility.Standard;
            navigationHotspotVisibility: HotspotVisibility = HotspotVisibility.Low;

            enableGyro: boolean = true;
          }
        </code>
      </pre>

          <h4>Floorplan Settings</h4>
          <pre>
        <code class="language-js">
          FloorplanSettings &#123;

            // plan presentation
            planScalingFactor: number; // scale for plan SVG elements
            planImageRotation: number; // degrees

            // fonts
            roomNameFontURL: string;
            roomNameFontFamily: string;
            roomNameFontSize: string;
            dimensionsFontURL: string;
            dimensionsFontFamily: string;

            //colour override for floor plan elements
            floorPlanColour: string;
            roomHighlightColour: string;
            defaultRoomHighlightColour: string;
            defaultOptionColour: string;
            optionColour: string;
            highlightOptions: boolean;

            roomHighlightOpacity: number;
            defaultRoomHighlightOpacity: number;

            //enable/disable highlighting of option walls in a different colour from non-option walls
            enableOptionHighlighting(enable: boolean);

            // Rotate the plan clockwise, valid values are 0, 90, 180, 270
            setPlanRotation(degrees: number): boolean;

            // Set the scale of SVG element in the user interface
            setPlanScalingFactor(scale: number);

            setFloorPlanColour(colour: string);

            setFloorPlanOptionColour(colour: string);

            setRoomHighlightColourAndOpacity(colour: string, opacity: string);

            setRoomNameFontURL(fontURL: string);

            setRoomNameFontFamily(fontFamily: string);

            setRoomNameFontSize(fontSize: string);

            setRoomDimensionsFontURL(fontURL: string);

            setRoomDimensionsFontFamily(fontFamily: string);

            getPlanScalingFactor(): number;
          }
        </code>
      </pre>

          <h3>Models</h3>
          <h4>Floors</h4>
          <pre>
        <code class="language-js">
          enum Floors &#123;
            GROUND = "GROUND",
            FIRST = "FIRST"
          }
        </code>
      </pre>

          <h4>RenderType</h4>
          <pre>
        <code class="language-js">
          enum RenderType &#123;
            Rasterized = 0,
            BestAvailable = 2
          }
        </code>
      </pre>

          <h4>EnvironmentName</h4>
          <pre>
        <code class="language-js">
          enum EnvironmentName &#123;
            Latest = 0,
            Staging = 1,
            Production = 2,
            Fallback = 3
          }
        </code>
      </pre>
      
      <h4>FieldOfViewType</h4>
      <pre>
        <code class="language-js">
          enum FieldOfViewType &#123;
            VFOV = "VFOV", //vertical field of view - based on the viewer window height.
            HFOV = "HFOV", //horizontal field of view - based on the viewer window width.
            DFOV = "DFOV", //diagonal field of view - based on the viewer window diagonal.
            MFOV = "MFOV", //maximum field of view - based either on the width or a mfovratio-scaled height, depending which size is larger.
            SFOV = "SFOV" //screen diagonal field of view - based on the total device screen diagonal, the viewer window size will not matter.
          }
        </code>
      </pre>

      <h4>HotspotVisibility</h4>
      <pre>
        <code class="language-js">
          enum HotspotVisibility &#123;
            Low = 0,
            Standard = 1,
            High = 2
          }
        </code>
      </pre>
          <div>
            <button mat-flat-button matStepperPrevious color="primary">Back</button>
            <button mat-flat-button (click)="stepper.reset()" color="warn">Reset</button>
          </div>
        </mat-step>
      </mat-vertical-stepper>
    </mat-card-content>
  </mat-card>
</div>
